/**
 * Read and validate a JSON value from browser storage.
 *
 * `JSON.parse` returns `any`, so reading straight into a typed slot asserts a
 * shape that nothing checked — and storage is genuinely untrusted input: it
 * survives deploys, so it can hold a shape written by an older version of the
 * app, or anything a user typed into devtools.
 *
 * Returns null when the entry is absent, unparseable, or fails `isValid`.
 */
export function parseStored<T>(
  raw: string | null,
  isValid: (value: unknown) => value is T,
): T | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return isValid(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** A plain object whose values are all booleans (e.g. collapsed-section state). */
export function isBooleanRecord(value: unknown): value is Record<string, boolean> {
  return (
    typeof value === 'object' && value !== null && !Array.isArray(value) &&
    Object.values(value).every(v => typeof v === 'boolean')
  );
}

/** A plain object whose values are all strings (e.g. stored UTM parameters). */
export function isStringRecord(value: unknown): value is Record<string, string> {
  return (
    typeof value === 'object' && value !== null && !Array.isArray(value) &&
    Object.values(value).every(v => typeof v === 'string')
  );
}
